import { CalendarDate, getLocalTimeZone, today } from '@internationalized/date';
import { useCalendar } from '@react-aria/calendar';
import { useDateField, useDatePicker } from '@react-aria/datepicker';
import { useLocale } from '@react-aria/i18n';
import { useCalendarState } from '@react-stately/calendar';
import { useDateFieldState, useDatePickerState } from '@react-stately/datepicker';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  calendarTitle,
  convertAriaDateToDate,
  convertDateToAriaDateTime,
  createCalendar,
  getDateValidation,
} from '../../../utils';

import type { RefObject } from 'react';

import type { DateTimePickerProps, UseDateTimePickerReturn } from './date-time-picker.types';

/**
 * Custom hook for the DateTimePicker component.
 *
 * This hook manages the interaction between the DateTimeInput, Calendar, and TimeSelectorPanel
 * components. It handles state synchronization, focus management, and event coordination
 * between the different parts of the date-time picker.
 *
 * ## Features
 *
 * - **Unified State Management**: Coordinates date and time state across components
 * - **Focus Management**: Handles focus trapping and keyboard navigation
 * - **Event Coordination**: Manages opening/closing and value updates
 * - **Input Integration**: Provides props for DateTimeInput component
 * - **Calendar Integration**: Manages calendar state and interactions
 * - **Time Integration**: Handles time selector state and changes
 *
 * @param props - DateTimePicker component props
 * @param ref - React ref to the container element
 * @returns Object with calendar state, input props, and time handling
 *
 * @example
 * ```tsx
 * const state = useDateTimePickerState(props);
 * const {
 *   calendarState,
 *   dateTimeInputProps,
 *   lockFocus,
 *   timeValue,
 *   onTimeChange
 * } = useDateTimePicker(props, state);
 * ```
 */
export const useDateTimePicker = (props: DateTimePickerProps, ref: RefObject<Element>): UseDateTimePickerReturn => {
  const {
    defaultValue,
    disabled,
    form,
    granularity = 'minute',
    helperText,
    hourCycle = 12,
    id,
    label,
    locale: localeProp,
    maxDate,
    minDate,
    name,
    onBlur,
    onChange,
    onFocus,
    onKeyDown,
    onKeyUp,
    onOpenChange,
    placeholderValue,
    readOnly,
    required,
    showTimezone = false,
    value,
  } = props;

  const { locale: defaultLocale } = useLocale();
  const locale = localeProp ?? defaultLocale;

  // Memoize converted date values to prevent infinite re-render loops.
  // convertDateToAriaDateTime creates a new object on every call, and React Aria's
  // state hooks trigger onChange when they receive a new value reference, causing
  // a render cascade: new ref → state update → onChange → setValue → new render.
  const ariaValue = useMemo(
    () => convertDateToAriaDateTime(value),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value?.getTime()],
  );
  const ariaDefaultValue = useMemo(
    () => convertDateToAriaDateTime(defaultValue),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultValue?.getTime()],
  );
  const ariaMinValue = useMemo(
    () => convertDateToAriaDateTime(minDate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [minDate?.getTime()],
  );
  const ariaMaxValue = useMemo(
    () => convertDateToAriaDateTime(maxDate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [maxDate?.getTime()],
  );
  const ariaPlaceholderValue = useMemo(
    () => convertDateToAriaDateTime(placeholderValue),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [placeholderValue?.getTime()],
  );

  // Initialize state management
  const state = useDatePickerState({
    // Convert JavaScript Date objects to React Aria's CalendarDateTime format
    // This preserves time information unlike convertDateToAriaDate which only handles dates
    defaultValue: ariaDefaultValue,
    granularity,
    hideTimeZone: !showTimezone,
    hourCycle,
    isDisabled: disabled,
    isReadOnly: readOnly,
    isRequired: required,
    maxValue: ariaMaxValue,
    minValue: ariaMinValue,
    onBlur,
    onChange: (newValue) => {
      // Convert React Aria's date/time back to JavaScript Date
      const newValueDate = convertAriaDateToDate(newValue);

      // Perform validation using the existing date validation utility
      const validationResult = getDateValidation(newValueDate, props, locale);

      // Call the user's onChange handler with the date and validation context
      onChange?.(newValueDate, {
        valid: validationResult.validityState.valid,
        ...validationResult,
      });
    },
    onFocus,
    onKeyDown,
    onKeyUp,
    onOpenChange,
    placeholderValue: ariaPlaceholderValue,
    shouldCloseOnSelect: false,
    validationBehavior: 'aria',
    value: ariaValue,
  });

  const useDatePickerReturn = useDatePicker(
    {
      'aria-label': typeof label === 'string' ? label : undefined,
      description: helperText,
      form,
      granularity,
      hideTimeZone: !showTimezone,
      hourCycle,
      id: id ?? undefined,
      isDisabled: disabled,
      isReadOnly: readOnly,
      isRequired: required,
      label,
      maxValue: ariaMaxValue,
      minValue: ariaMinValue,
      name,
      onBlur,
      onFocus,
      onKeyDown,
      onKeyUp,
      onOpenChange,
      placeholderValue: ariaPlaceholderValue,
      shouldForceLeadingZeros: true,
      validationBehavior: 'aria',
    },
    state,
    ref,
  );

  const { calendarProps: _calendarProps, dialogProps, fieldProps, groupProps } = useDatePickerReturn;

  // Date input related props
  const dateFieldRef = useRef(null);
  const dateFieldInputRef = useRef<HTMLInputElement>(null);

  const dateFieldState = useDateFieldState({
    ...fieldProps,
    createCalendar,
    locale,
    maxValue: ariaMaxValue,
    minValue: ariaMinValue,
    placeholderValue: ariaPlaceholderValue,
    value: state.value,
  });

  const useDateFieldReturn = useDateField(
    {
      'aria-label': typeof label === 'string' ? label : undefined,
      description: helperText,
      form,
      granularity,
      hideTimeZone: !showTimezone,
      hourCycle,
      inputRef: dateFieldInputRef,
      isDisabled: disabled,
      isReadOnly: readOnly,
      isRequired: required,
      label,
      name,
      onBlur,
      onFocus,
      onKeyDown,
      onKeyUp,
      validationBehavior: 'aria',
    },
    dateFieldState,
    dateFieldRef,
  );
  const { fieldProps: dateFieldProps, inputProps } = useDateFieldReturn;

  const hiddenInputProps = {
    ...inputProps,
    disabled,
    readOnly,
    required,
  };

  // Calendar related props
  const initialFocusedValue = state.value ?? ariaPlaceholderValue ?? today(getLocalTimeZone());
  const [focusedValue, setFocusedValue] = useState(initialFocusedValue);

  const calendarRef = useRef<HTMLDivElement>(null);
  const calendarState = useCalendarState({
    ..._calendarProps,
    createCalendar,
    focusedValue,
    isDisabled: disabled,
    isReadOnly: readOnly,
    locale,
    onChange: (value) => {
      // This was commented out because it was causing the calendar to not update when the value was set to null when the granularity was not 'day'
      // _calendarProps?.onChange?.(value);

      state.setValue(value);

      if (value instanceof CalendarDate) {
        const date = value.toDate(getLocalTimeZone());
        const newValue = convertDateToAriaDateTime(date);
        state.setValue(newValue);
      }
    },
    onFocusChange: (date) => {
      _calendarProps?.onFocusChange?.(date);
      if (date) {
        setFocusedValue(date);
      }
    },
  });

  useEffect(() => {
    if (state.value) {
      // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
      setFocusedValue(state.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.value?.toString()]);

  // @ts-expect-error calendarRef is not defined
  const useCalendarReturn = useCalendar({}, calendarState, calendarRef);
  const {
    calendarProps,
    nextButtonProps: _nextButtonProps,
    prevButtonProps: _prevButtonProps,
    title: ariaTitle,
  } = useCalendarReturn;
  const title = calendarTitle(ariaTitle);

  const nextButtonProps = {
    'aria-label': 'Next month',
    disabled: _nextButtonProps.isDisabled,
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
      calendarState.focusNextPage();
      e.stopPropagation();
    },
  };

  const prevButtonProps = {
    'aria-label': 'Previous month',
    disabled: _prevButtonProps.isDisabled,
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
      calendarState.focusPreviousPage();
      e.stopPropagation();
    },
  };

  // Time management for the selected date/time
  const timeValue = state.value ? convertAriaDateToDate(state.value) : null;

  /**
   * Handle time changes from the time selector panel.
   * Updates the state value while preserving date components.
   */
  const handleTimeChange = (newTime: Date): void => {
    const newAriaDate = convertDateToAriaDateTime(newTime);
    state.setValue(newAriaDate);
  };

  return {
    calendarProps: {
      calendarProps,
      calendarRef,
      calendarState,
      nextButtonProps,
      prevButtonProps,
      title,
    },
    dateFieldProps: {
      dateFieldInputRef,
      dateFieldProps,
      dateFieldRef,
      dateFieldState,
      groupProps,
      hiddenInputProps,
    },
    datePickerState: state,
    popupProps: {
      dialogProps,
    },
    timeFieldProps: {
      onTimeChange: handleTimeChange,
      timeValue,
    },
  };
};
